import { Fragment, Suspense, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { SidebarItem, useSidebarOpenState } from '@backstage/core-components';
import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Link, useMatch } from 'react-router-dom';
import { useAnalytics } from '@backstage/core-plugin-api';
import {
  iconsApiRef,
  useApi,
  useRouteRef,
  useTranslationRef,
} from '@backstage/frontend-plugin-api';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import {
  branchOptions,
  healthColor,
  healthLabel,
} from '../projectEnvironments/argoApp';
import {
  branchActivity,
  compareBranches,
  isEphemeralBranch,
} from '../projectEnvironments/branchKind';
import {
  sleepingBranches,
  usePermanentEnvironments,
} from '../projectEnvironments/permanentEnvironments';
import { waadooIcons } from '../ui/icons/waadooIcons';
import {
  DELETION_POLL_MAX_AGE_MS,
  deletingBranches,
  useDeletionPolling,
  useEphemeralBranchLists,
} from '../projectEnvironments/branchDeletion';
import { usePendingDeletions } from '../projectEnvironments/pendingDeletions';
import {
  BranchLabel,
  useBranchDescription,
} from '../projectEnvironments/BranchLabel';
import { useBranchMeta } from '../projectEnvironments/branchManagement';
import {
  BRANCH_PAGE_PATH,
  branchRouteRef,
} from '../projectEnvironments/branchRouteRef';
import {
  applicationsByProject,
  loadAllApplications,
} from '../projectEnvironments/projectApplications';
import { projectEnvironmentsTranslationRef } from '../projectEnvironments/translation';
import { useProjectAccess } from '../projectEnvironments/projectAccess';
import { ReadOnlyBadge } from '../projectEnvironments/ReadOnlyBadge';
import { CompactProjectsNav } from './CompactProjectsNav';
import { healthDotColors } from './compactProjects';
import { useProjectNavReads } from './useProjectNavReads';

const SleepingIcon = waadooIcons.sleeping;

/**
 * How many ephemeral branches the open project shows before the « + N autres »
 * toggle — a design choice (sidebar redesign of 2026-10-03), so that a project
 * at its limit of ten does not stretch the menu twice as long.
 */
export const VISIBLE_EPHEMERAL_BRANCHES = 5;

/** Which project was expanded last, so a reload reopens the same one. */
const STORAGE_KEY = '@waadoo/projects-nav/expanded-project';

function readStoredProject(): string | undefined {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? undefined;
  } catch {
    // Guarded the way Backstage guards its own sidebar pin state
    // (core-components' Sidebar/localStorage.esm.js): reading localStorage
    // throws outright in a browser where storage is disabled, and losing the
    // last-opened project must not take the whole sidebar down with it.
    return undefined;
  }
}

function writeStoredProject(projectId: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, projectId);
  } catch {
    // Same reason as above, plus the quota: this is a convenience.
  }
}

const useStyles = makeStyles<Theme>(theme => ({
  sectionTitle: {
    color: theme.palette.navigation.color,
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 700,
    letterSpacing: 1,
    opacity: 0.7,
    padding: theme.spacing(1.5, 2, 0.5, 3),
    textTransform: 'uppercase',
  },
  statusRow: {
    alignItems: 'center',
    color: theme.palette.navigation.color,
    display: 'flex',
    fontSize: theme.typography.pxToRem(12),
    opacity: 0.7,
    padding: theme.spacing(0.5, 2, 0.5, 6),
  },
  statusSpinner: {
    marginRight: theme.spacing(1),
  },
}));

function ProjectsNavContent() {
  const classes = useStyles();
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);

  const iconsApi = useApi(iconsApiRef);
  const describeBranch = useBranchDescription();
  // « Lecture seule » on the projects the user is only a member of
  // (ReadOnlyBadge.tsx): one request for the whole list, shared with the page.
  const levelOf = useProjectAccess();
  // core-plugin-api's, the one SidebarItem itself reports clicks through
  // (core-components 0.18.14, Items.esm.js), so the branch rows' events land
  // where every other sidebar click does.
  const analytics = useAnalytics();

  const entityRoute = useRouteRef(entityRouteRef);
  const branchRoute = useRouteRef(branchRouteRef);
  // A boolean and not `branchRoute` itself in the reads' effect: useRouteRef
  // memoises on the current pathname, so the function's identity changes on
  // every in-app navigation, and an effect depending on it would re-run — and
  // re-query ArgoCD — each time the user moves. Whether the route exists at
  // all does not change while the app is running.
  const canLinkBranches = Boolean(branchRoute);

  // The URL is the second source of truth for "which project": on
  // /projets/<project>/<branch> the sidebar opens that project even though
  // nothing was clicked. Matched against the very literal the page extension
  // mounts on, so the two cannot drift apart.
  const branchMatch = useMatch(BRANCH_PAGE_PATH);

  const [clickedProject, setClickedProject] = useState<string>();
  // Read once, at mount: afterwards the click and the URL decide, and re-reading
  // storage could only fight them.
  const [lastVisitedProject] = useState(readStoredProject);
  // The project whose ephemeral branches are all shown, past the first
  // VISIBLE_EPHEMERAL_BRANCHES. Keyed by project, so opening another one
  // starts collapsed.
  const [ephemeralExpandedFor, setEphemeralExpandedFor] = useState<string>();

  // Exactly one project open, and this expression is the whole rule: the last
  // click wins, then the branch page one is reading, then the last visit.
  const expandedProject =
    clickedProject ?? branchMatch?.params.project ?? lastVisitedProject;

  // The environments are read on expand only (`useProjectNavReads`), keyed by
  // the open project so that opening another one retries a failed read.
  const {
    projects,
    projectsLoading,
    appsByProject,
    setAppsByProject,
    branchesLoading,
    appsRequest,
  } = useProjectNavReads(
    expandedProject && canLinkBranches ? expandedProject : undefined,
  );

  useEffect(() => {
    if (expandedProject) {
      writeStoredProject(expandedProject);
    }
  }, [expandedProject]);

  // « Suppression en cours » (branchDeletion.ts), for the open project only —
  // the only one whose branches are shown. Its git list is asked only when
  // it shows an ephemeral environment.
  const [deletionPoll, setDeletionPoll] = useState(0);
  const pendingDeletions = usePendingDeletions();
  const expandedBranches =
    expandedProject && appsByProject
      ? branchOptions(appsByProject.get(expandedProject) ?? [])
      : [];
  const gitLists = useEphemeralBranchLists(
    expandedProject &&
      expandedBranches.some(option => isEphemeralBranch(option.branch))
      ? [expandedProject]
      : [],
    deletionPoll,
  );
  const deleting = expandedProject
    ? deletingBranches(expandedBranches, {
        project: expandedProject,
        git: gitLists.get(expandedProject),
        pending: pendingDeletions,
        now: Date.now(),
      })
    : new Set<string>();
  // While a branch shown is being deleted: the ArgoCD list again — shared
  // with the home page's poll (`DELETION_POLL_MAX_AGE_MS`) — and the git
  // list; a branch whose environment is gone leaves the list with it. A
  // failed read is swallowed, for the reason given on the reads above.
  useDeletionPolling(deleting.size > 0, () => {
    setDeletionPoll(current => current + 1);
    const poll = async () => {
      try {
        const apps = await loadAllApplications(appsRequest(), {
          maxAgeMs: DELETION_POLL_MAX_AGE_MS,
        });
        setAppsByProject(applicationsByProject(apps));
      } catch {
        // The next tick tries again.
      }
    };
    // Returned: the next tick waits for this one (`useVisiblePolling`).
    return poll();
  });

  // The per-project limit of the « Éphémères · N/max » label, asked only
  // while the open project shows an ephemeral branch: the label is hidden
  // otherwise. Undefined on any failure; the label then gives the count alone.
  const { meta: branchMeta } = useBranchMeta(
    expandedProject &&
      expandedBranches.some(option => isEphemeralBranch(option.branch))
      ? expandedProject
      : undefined,
  );

  // « En sommeil » (`permanentEnvironments.ts`): the permanent branches the
  // open project's appenv repository declares and nothing is deployed for.
  // Asked for the open project only, and only once its environments are
  // listed — never at startup, nor once per project.
  const permanentEnvironments = usePermanentEnvironments(
    expandedProject && canLinkBranches && appsByProject
      ? expandedProject
      : undefined,
  );
  const expandedSleeping = appsByProject
    ? sleepingBranches(
        permanentEnvironments ?? [],
        expandedBranches.map(option => option.branch),
      )
    : [];

  const statusDotIcon = iconsApi.icon('status-dot');

  const renderBranches = (projectId: string): ReactNode => {
    // Same guard as BranchSwitcher: undefined when the `odoo` page plugin is
    // not registered. A branch row that cannot link anywhere is not worth a
    // line, so the project keeps its own row and gains nothing under it.
    if (!branchRoute) {
      return null;
    }

    if (branchesLoading) {
      return (
        <div className={classes.statusRow}>
          <CircularProgress
            size={12}
            color="inherit"
            className={classes.statusSpinner}
          />
          {t('nav.loadingBranches')}
        </div>
      );
    }

    if (!appsByProject) {
      return null;
    }

    // A project ArgoCD lists nothing for is a project without environment,
    // exactly what the per-project selector used to answer with `items: null`.
    const branches = branchOptions(appsByProject.get(projectId) ?? []);
    // Computed for the open project, the only one this renders for.
    const sleeping = projectId === expandedProject ? expandedSleeping : [];

    if (branches.length === 0 && sleeping.length === 0) {
      return <div className={classes.statusRow}>{t('nav.noBranches')}</div>;
    }

    // Sorted by branchOptions: by tier (prod, the staging branches, dev,
    // then the ephemeral ones), each tier by name.
    const permanent = branches.filter(
      option => !isEphemeralBranch(option.branch),
    );
    const ephemeral = branches.filter(option =>
      isEphemeralBranch(option.branch),
    );
    // The branch page being read, in this project. The same match that opens
    // the project (`expandedProject`): the page keeps its tab in the query
    // string, so the path is exactly /projets/<project>/<branch>.
    const activeBranch =
      branchMatch?.params.project === projectId
        ? branchMatch.params.branch
        : undefined;

    const showAll = ephemeralExpandedFor === projectId;
    // Collapsed, the first five — plus the open branch when it is further
    // down, so the row of the page being read never disappears.
    const shownEphemeral = showAll
      ? ephemeral
      : ephemeral.filter(
          (option, index) =>
            index < VISIBLE_EPHEMERAL_BRANCHES ||
            option.branch === activeBranch,
        );
    const collapsedCount = ephemeral.length - shownEphemeral.length;
    // Offered whenever there is something to fold or unfold: not when the
    // only branch past the fifth is the open one, already shown.
    // The visible words, then the context a screen reader needs, hidden on
    // screen: the accessible name starts with the visible label (WCAG 2.5.3).
    let toggle: { text: string; context: string } | undefined;
    if (showAll && ephemeral.length > VISIBLE_EPHEMERAL_BRANCHES) {
      toggle = {
        text: t('nav.lessEphemeral'),
        context: t('nav.lessEphemeralContext'),
      };
    } else if (collapsedCount === 1) {
      toggle = {
        text: t('nav.moreEphemeralOne'),
        context: t('nav.moreEphemeralOneContext'),
      };
    } else if (collapsedCount > 1) {
      toggle = {
        text: t('nav.moreEphemeral', { hidden: String(collapsedCount) }),
        context: t('nav.moreEphemeralContext'),
      };
    }

    const now = Date.now();
    const renderRow = (option: (typeof branches)[number]) => {
      const isDeleting = deleting.has(option.branch);
      const description = isDeleting
        ? t('deletion.badge')
        : describeBranch(branchActivity(option.branch, option, now));
      const to = branchRoute({ project: projectId, branch: option.branch });
      // The description of an ephemeral branch already says « Environnement
      // éphémère » — except « Suppression en cours », which then gets the
      // kind in words.
      let label = option.branch;
      if (isDeleting && isEphemeralBranch(option.branch)) {
        label = t('nav.ephemeralBranchLabel', {
          branch: option.branch,
          description: t('deletion.badge'),
        });
      } else if (description) {
        label = t('nav.branchLabel', { branch: option.branch, description });
      }
      // The dot alone would be colour alone (WCAG 1.4.1): any health but the
      // nominal one is said in words too, with ArgoCD's labels of the card.
      if (option.health !== 'Healthy') {
        label = `${label}, ${healthLabel(option.health, key => t(key))}`;
      }
      const active = option.branch === activeBranch;
      const tone = healthColor(option.health);
      return (
        <li key={option.branch}>
          <Link
            to={to}
            className="waadoo-nav-branch"
            aria-current={active ? 'page' : undefined}
            // The visible text is the name alone, ellipsized; the name says
            // the kind, the state and a health that is not Healthy too. The
            // same words as a tooltip: the full name of a clipped one, the
            // age the badge no longer shows here, the dot's meaning.
            aria-label={label}
            title={label}
            // The event SidebarItem would have sent (core-components 0.18.14,
            // Items.esm.js): `click`, the row's text as subject, `to` as
            // attribute — the branch rows' clicks land where every other
            // sidebar click does.
            onClick={() =>
              analytics.captureEvent('click', option.branch, {
                attributes: { to },
              })
            }
          >
            <BranchLabel
              branch={option.branch}
              timing={option}
              now={now}
              variant="sidebar"
              deleting={isDeleting}
            />
            {/* Decorative: the svg is registered `aria-hidden`
                (modules/icons.tsx), and the health it shows is in the
                row's aria-label and title above. */}
            <span
              className="waadoo-nav-branch__dot"
              data-health={tone}
              style={{ color: healthDotColors[tone] }}
            >
              {/* Registered in modules/icons.tsx: icon() is only typed as
                  possibly undefined, for a name nobody registered. */}
              {statusDotIcon ?? null}
            </span>
          </Link>
        </li>
      );
    };

    // A sleeping branch: muted, the moon in place of the health dot, its
    // state in its accessible name; still a way to its branch page.
    const renderSleepingRow = (branch: string) => {
      const to = branchRoute({ project: projectId, branch });
      const label = t('nav.branchLabel', {
        branch,
        description: t('sleeping.badge'),
      });
      return (
        <li key={branch}>
          <Link
            to={to}
            className="waadoo-nav-branch"
            aria-current={branch === activeBranch ? 'page' : undefined}
            aria-label={label}
            title={label}
            onClick={() =>
              analytics.captureEvent('click', branch, { attributes: { to } })
            }
          >
            <BranchLabel branch={branch} variant="sidebar" sleeping />
            {/* Decorative: the row's name says « En sommeil ». */}
            <span className="waadoo-nav-branch__dot">
              <SleepingIcon aria-hidden focusable="false" />
            </span>
          </Link>
        </li>
      );
    };
    // The deployed permanent branches and the sleeping ones, by tier then
    // by name (`compareBranches`).
    const permanentRows = [
      ...permanent.map(option => ({ branch: option.branch, option })),
      ...sleeping.map(branch => ({ branch, option: undefined })),
    ].sort((a, b) => compareBranches(a.branch, b.branch));

    const groupLabelId = `waadoo-nav-ephemeral-${projectId}`;
    const ephemeralListId = `waadoo-nav-ephemeral-list-${projectId}`;
    // The git count when the meta route gave it — the one the creation limit
    // is checked against, so « 3/10 » here matches the creation dialog;
    // otherwise (route absent, failed, or a non-editor, who gets no count)
    // the environments listed, without a limit beside them.
    const gitCount = branchMeta?.ephemeralCount ?? undefined;
    return (
      <div className="waadoo-nav-branches">
        {permanentRows.length > 0 ? (
          <ul className="waadoo-nav-branches__list">
            {permanentRows.map(({ branch, option }) =>
              option ? renderRow(option) : renderSleepingRow(branch),
            )}
          </ul>
        ) : null}
        {ephemeral.length > 0 ? (
          <>
            <div className="waadoo-nav-branches__group" id={groupLabelId}>
              {branchMeta && gitCount !== undefined
                ? t('nav.ephemeralGroup', {
                    used: String(gitCount),
                    max: String(branchMeta.max),
                  })
                : t('nav.ephemeralGroupNoMax', {
                    used: String(ephemeral.length),
                  })}
            </div>
            <ul
              id={ephemeralListId}
              className="waadoo-nav-branches__list"
              aria-labelledby={groupLabelId}
            >
              {shownEphemeral.map(renderRow)}
            </ul>
            {toggle ? (
              <button
                type="button"
                className="waadoo-nav-branches__more"
                aria-expanded={showAll}
                aria-controls={ephemeralListId}
                onClick={() =>
                  setEphemeralExpandedFor(showAll ? undefined : projectId)
                }
              >
                {/* The space as its own text node: inside the hidden span, it
                    was dropped from the computed name (« + 2 moreephemeral
                    branches » in dom-accessibility-api, which Testing
                    Library uses) — the trimming BranchKindBadge works around
                    with a comma. */}
                {toggle.text}{' '}
                <span className="waadoo-visually-hidden">{toggle.context}</span>
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    );
  };

  // `entityRoute` is undefined when the catalog plugin is not registered, which
  // is also the plugin every project row points into: without it the block has
  // nowhere to lead.
  if (!entityRoute) {
    return null;
  }

  // Nothing to show and nothing to say: no title, no empty area. This is also
  // what a signed-out visitor gets, the catalog read above having failed.
  if (!projectsLoading && projects.length === 0) {
    return null;
  }

  return (
    <>
      <Typography component="div" className={classes.sectionTitle}>
        {t('nav.projects')}
      </Typography>
      {projectsLoading ? (
        <div className={classes.statusRow}>
          <CircularProgress
            size={12}
            color="inherit"
            className={classes.statusSpinner}
          />
        </div>
      ) : (
        projects.map(project => (
          <Fragment key={project.entityRef}>
            {/* `to` and `onClick` together: SidebarItem's link variant runs the
                handler and follows the link, which is exactly "open this
                project and go to it". */}
            {/* The badge goes in SidebarItem's children, the fixed-width
                slot it renders after the label (core-components 0.18.14,
                Items.esm.js, `secondaryAction`, present on every row): the
                label keeps its width and the rows their alignment. Inside
                the link, so its words go in the link's name — SidebarItem
                sets `aria-label` to `text`, then spreads the NavLink props,
                which therefore override it. */}
            <SidebarItem
              icon={
                project.projectId === expandedProject
                  ? ExpandMoreIcon
                  : ChevronRightIcon
              }
              text={project.label}
              to={entityRoute(project.params)}
              onClick={() => setClickedProject(project.projectId)}
              aria-label={
                levelOf(project.projectId) === 'read'
                  ? t('readOnly.navProject', { project: project.label })
                  : project.label
              }
            >
              <ReadOnlyBadge project={project.projectId} variant="sidebar" />
            </SidebarItem>
            {project.projectId === expandedProject
              ? renderBranches(project.projectId)
              : null}
          </Fragment>
        ))
      )}
    </>
  );
}

/**
 * The Odoo projects of the catalog in the main sidebar, the current one opened
 * on its branches.
 *
 * Wrapped in its own Suspense boundary: `useTranslationRef` throws a promise
 * until the ref's resource has loaded, and the nearest boundary above the
 * sidebar is the one `createApp` puts at the root — so without this the whole
 * app would blank to its loading indicator for a tick on every cold start.
 */
export function ProjectsNav() {
  // Closed — the compact menu (compactNav.ts) — one badge per project
  // instead of the names (CompactProjectsNav.tsx).
  const { isOpen } = useSidebarOpenState();
  return (
    <Suspense fallback={null}>
      {isOpen ? <ProjectsNavContent /> : <CompactProjectsNav />}
    </Suspense>
  );
}
